/**
 * Error types for the Canvas client.
 *
 * These exist so the UI can distinguish "your token is bad" from "the network
 * is down" from "Canvas said no" — three failures that need three different
 * messages. A single generic Error would collapse them into one unhelpful
 * "something went wrong", which for a submission screen is the worst possible
 * outcome (a silently failed homework submission is the bug this project most
 * needs to avoid).
 */
import { type CanvasErrorBody } from './types';

export class CanvasError extends Error {
  constructor(
    message: string,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'CanvasError';
  }
}

/** A non-2xx response from Canvas (or from the proxy). */
export class CanvasHttpError extends CanvasError {
  constructor(
    readonly status: number,
    readonly statusText: string,
    readonly url: string,
    readonly body: CanvasErrorBody | string | null,
    message?: string,
  ) {
    super(message ?? CanvasHttpError.describe(status, statusText, body));
    this.name = 'CanvasHttpError';
  }

  /** True when retrying the identical request could plausibly succeed. */
  get isRetryable(): boolean {
    return this.status === 429 || this.status === 408 || this.status >= 500;
  }

  get isAuthError(): boolean {
    return this.status === 401;
  }

  get isPermissionError(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  private static describe(
    status: number,
    statusText: string,
    body: CanvasErrorBody | string | null,
  ): string {
    const detail = CanvasHttpError.extractMessage(body);
    switch (status) {
      case 401:
        return detail ?? 'Canvas rejected the access token. It may be expired or revoked.';
      case 403:
        return detail ?? 'Canvas refused this request. You may not have access to this item.';
      case 404:
        return detail ?? 'Canvas could not find that item.';
      case 503:
        return detail ?? 'The proxy is not configured. Check CANVAS_ORIGIN — see DEPLOY.md.';
      default:
        return detail ?? `Canvas returned ${status} ${statusText}`.trim();
    }
  }

  /** Canvas uses several error envelope shapes; pull a human string from any of them. */
  static extractMessage(body: CanvasErrorBody | string | null): string | null {
    if (!body) return null;
    if (typeof body === 'string') return body.slice(0, 500) || null;
    if (typeof body.message === 'string') return body.message;
    if (typeof body.error === 'string') return body.error;
    if (Array.isArray(body.errors)) {
      const messages = body.errors.map((e) => e.message).filter(Boolean);
      if (messages.length) return messages.join('; ');
    } else if (body.errors && typeof body.errors === 'object') {
      // Field-keyed shape: { errors: { assignment: [{ message: '...' }] } }
      const collected: string[] = [];
      for (const value of Object.values(body.errors)) {
        if (Array.isArray(value)) {
          for (const entry of value) {
            if (entry && typeof entry === 'object' && 'message' in entry) {
              collected.push(String((entry as { message: unknown }).message));
            }
          }
        }
      }
      if (collected.length) return collected.join('; ');
    }
    return null;
  }
}

/** The request never got a response: offline, DNS, TLS, or a blocked host. */
export class CanvasNetworkError extends CanvasError {
  constructor(
    readonly url: string,
    cause?: unknown,
  ) {
    super(
      'Could not reach Canvas. Check your connection — if you are on school wifi, ' +
        'the proxy domain may be blocked.',
      cause,
    );
    this.name = 'CanvasNetworkError';
  }
}

/** No token has been stored yet, so the app cannot call Canvas at all. */
export class CanvasNoTokenError extends CanvasError {
  constructor() {
    super('No Canvas access token is stored on this device. Add one in Settings.');
    this.name = 'CanvasNoTokenError';
  }
}

/**
 * ############################################################
 * The guard from brainstorm.md 1.19 firing.
 *
 * A mutating request (POST/PUT/PATCH/DELETE) was attempted against a
 * host that is not the mock, while `allowLiveWrites` was false. The
 * request was NOT sent — this is thrown before construction.
 *
 * If you are seeing this and you meant it, you are the human who has
 * to flip the flag by hand. That is the design.
 * ############################################################
 */
export class LiveWriteBlockedError extends CanvasError {
  constructor(
    readonly method: string,
    readonly url: string,
  ) {
    super(
      `BLOCKED: refused to send ${method} to a live Canvas host (${url}). ` +
        'Writes to a live host are disabled unless allowLiveWrites is explicitly ' +
        'enabled, which no code in this app does. This guard exists because the ' +
        'target account belongs to a real student with real grades in it. ' +
        'See brainstorm.md 1.19.',
    );
    this.name = 'LiveWriteBlockedError';
  }
}

/** Canvas rate limiting kicked in. */
export class CanvasRateLimitError extends CanvasError {
  constructor(readonly url: string) {
    super('Canvas is rate limiting requests. Slowing down and retrying.');
    this.name = 'CanvasRateLimitError';
  }
}
